import React, { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Shield } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../../contexts/AuthContext'
import authAPI from '../../lib/authAPI'

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)
const isNonEmptyString = value => typeof value === 'string' && value.length > 0

const decodeJwtPayload = token => {
  const parts = token.split('.')
  if (parts.length !== 3 || !parts.every(isNonEmptyString)) {
    throw new Error('Malformed access token')
  }

  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  return JSON.parse(window.atob(padded))
}

const getStrictUpgradedSession = response => {
  const session = response?.data?.session
  if (
    !isObject(response) ||
    response.error !== null ||
    !isObject(response.data) ||
    !isObject(session) ||
    !isNonEmptyString(session.access_token) ||
    !isNonEmptyString(session.refresh_token) ||
    typeof session.token_type !== 'string' ||
    session.token_type.toLowerCase() !== 'bearer' ||
    !Number.isFinite(session.expires_in) ||
    session.expires_in <= 0 ||
    !isObject(session.user) ||
    !isNonEmptyString(session.user.id)
  ) {
    throw new Error('Malformed upgraded session')
  }

  const claims = decodeJwtPayload(session.access_token)
  if (!isObject(claims) || claims.aal !== 'aal2') {
    throw new Error('Session was not upgraded to AAL2')
  }

  return session
}

const AuthMFA = ({ onSuccess }) => {
  const { signOut } = useAuth()
  const [verifyCode, setVerifyCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const handleSignOut = async () => {
    if (signingOut || loading) return
    setSigningOut(true)
    setError('')

    try {
      if (typeof signOut !== 'function') {
        throw new Error('Application sign out is unavailable')
      }
      const result = await signOut()
      if (result?.error) throw result.error
    } catch (signOutError) {
      console.error('🔐 AuthMFA: Sign out failed:', signOutError)
      setError('Sign out failed. Please try again.')
    } finally {
      setSigningOut(false)
    }
  }

  const onSubmitClicked = async () => {
    setError('')
    setLoading(true)
    
    try {
      console.log('🔐 AuthMFA: Starting MFA verification...')
      
      // Step 1: Get available MFA factors
      const factors = await supabase.auth.mfa.listFactors()
      if (
        !isObject(factors) ||
        factors.error !== null ||
        !isObject(factors.data) ||
        !Array.isArray(factors.data.totp)
      ) {
        throw factors?.error || new Error('Unable to determine MFA factors')
      }
      
      console.log('🔐 AuthMFA: Factors response:', factors.data)
      
      // Step 2: Find the TOTP factor (using official structure)
      const totpFactor = factors.data.totp[0]
      if (!totpFactor) {
        throw new Error('No TOTP factors found!')
      }
      
      console.log('🔐 AuthMFA: Using TOTP factor:', totpFactor.id)
      
      const factorId = totpFactor.id
      
      // Step 3: Create and verify the factor challenge
      const verify = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: verifyCode,
      })
      
      if (!isObject(verify) || verify.error !== null || !isObject(verify.data)) {
        throw verify?.error || new Error('MFA verification failed')
      }

      // Step 4: Read the upgraded Supabase session and sync application tokens
      try {
        const upgradedSession = getStrictUpgradedSession(await supabase.auth.getSession())
        authAPI.saveTokensToStorage(
          upgradedSession.access_token,
          upgradedSession.refresh_token
        )
        if (
          authAPI.token !== upgradedSession.access_token ||
          authAPI.refreshToken !== upgradedSession.refresh_token
        ) {
          throw new Error('Application token synchronization failed')
        }
      } catch (sessionError) {
        console.error('🔐 AuthMFA: Upgraded session validation failed:', sessionError)
        throw new Error('Unable to confirm the upgraded session. Please sign out and try again.')
      }
      
      console.log('🔐 AuthMFA: Verification successful!')
      toast.success('MFA verification successful!')
      
      // Success - call the callback to proceed to app
      await onSuccess()
      
    } catch (error) {
      console.error('🔐 AuthMFA: Error:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Version indicator */}
      <div className="absolute top-4 right-4 bg-purple-100 text-purple-800 px-3 py-2 rounded text-xs font-mono">
        <div className="font-bold">MFA Challenge v1.0</div>
        <div className="text-xs mt-1">✅ Official Supabase Pattern</div>
      </div>
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Security Verification Required
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Please enter the code from your authenticator app
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          
          <div className="space-y-6">
            <div>
              <label htmlFor="verify-code" className="block text-sm font-medium text-gray-700">
                Verification Code
              </label>
              <div className="mt-1">
                <input
                  id="verify-code"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.trim())}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-center text-lg font-mono"
                  placeholder="000000"
                  maxLength={6}
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <button
                onClick={onSubmitClicked}
                disabled={loading || !verifyCode}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>

          <div className="mt-6">
            <div className="text-center">
              <p className="text-xs text-gray-500">
                Can't access your authenticator app? Contact your administrator.
              </p>
              <button
                type="button"
                onClick={handleSignOut}
                disabled={loading || signingOut}
                className="mt-4 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
              >
                {signingOut ? 'Signing out...' : 'Sign out'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthMFA
